import { HttpMethod } from 'constants/httpMethod'
import { Query, QueryResponse } from 'types'
import { identity, pick } from 'utils'
import { useRequest } from '../useRequest'
import { useServiceCountryUrl } from '../useServiceCountryUrl'
import { Role, User, UserSettings } from './types'

const USER_SERVICE_REQUEST_CONFIG = {
  headers: {
    'x-request-client-id': 'ta.partner-portal',
  },
}

const getConfig = () => USER_SERVICE_REQUEST_CONFIG

enum ApiPathname {
  // Not all endpoints are implemented here
  USERS = '/api/v1/users',
  USER = '/api/v1/users/:userId',
  USER_SETTINGS = '/api/v1/users/:userId/settings',
  CURRENT_USER = '/api/v1/users/me',
}

type GetUserQuery = Query<{
  userId: string
}>

type GetUsersQuery = Query<{
  search?: string
  email?: string[]
  familyName?: string[]
  givenName?: string[]
  id?: string[]
  gid?: string[]
  isActive?: boolean[]
  roles?: Role[]
  createdAt?: string[]
  createdBy?: string[]
  updatedAt?: string[]
  updatedBy?: string[]
  limit?: number
  offset?: number
  sort?: ('ASC' | 'DESC')[]
  sortBy?: Array<
    | 'createdAt'
    | 'createdBy'
    | 'email'
    | 'familyName'
    | 'gid'
    | 'givenName'
    | 'id'
    | 'isActive'
    | 'name'
    | 'updatedAt'
    | 'updatedBy'
  >
}>

type GetUserResponse = User
type GetUsersResponse = QueryResponse<User[]>
type GetUserSettingsResponse = UserSettings

type UpdateUserSettingsRequest = {
  userId: string
  settings: Partial<UserSettings>
}

interface UseHookContext {
  getUser: (query: GetUserQuery) => Promise<GetUserResponse>
  getUsers: (query: GetUsersQuery) => Promise<GetUsersResponse>
  getUserSettings: (query: GetUserQuery) => Promise<GetUserSettingsResponse>
  updateUserSettings: (request: UpdateUserSettingsRequest) => Promise<UserSettings>
  getCurrentUser: () => Promise<GetUserResponse>
}

const useUserService = (): UseHookContext => {
  const { url: baseUrl } = useServiceCountryUrl('user')

  const getUser = useRequest<GetUserQuery, GetUserResponse>({
    baseUrl,
    filterQueryToRouteParams: pick(['userId']),
    getConfig,
    pathname: ApiPathname.USER,
  })

  const getUsers = useRequest<GetUsersQuery, GetUsersResponse>({
    baseUrl,
    filterQueryToSearchParams: identity,
    getConfig,
    pathname: ApiPathname.USERS,
  })

  const getUserSettings = useRequest<GetUserQuery, GetUserSettingsResponse>({
    baseUrl,
    filterQueryToRouteParams: pick(['userId']),
    getConfig,
    pathname: ApiPathname.USER_SETTINGS,
  })

  const updateUserSettings = useRequest<UpdateUserSettingsRequest, UserSettings>({
    baseUrl,
    filterQueryToRequestBody: pick(['settings']),
    filterQueryToRouteParams: pick(['userId']),
    getConfig,
    method: HttpMethod.PATCH,
    pathname: ApiPathname.USER_SETTINGS,
  })

  const getCurrentUser = useRequest<void, GetUserResponse>({
    baseUrl,
    getConfig,
    pathname: ApiPathname.CURRENT_USER,
  })

  return {
    getCurrentUser,
    getUser,
    getUserSettings,
    getUsers,
    updateUserSettings,
  }
}

export { useUserService }
export type { GetUserQuery, GetUsersQuery, Role, User }
